import type {
  CommissionEntryRow,
  CommissionFilters,
  CommissionGroupRow,
  PeriodInfo,
} from './types';

function buildQuery(filters: CommissionFilters): string {
  const p = new URLSearchParams();
  p.set('period', filters.period);
  for (const s of filters.status) p.append('status', s);
  for (const r of filters.salesRepIds) p.append('sales_rep', r);
  for (const t of filters.entityTypes) p.append('entity_type', t);
  return p.toString();
}

export async function fetchGroups(
  filters: CommissionFilters,
): Promise<{ period: PeriodInfo; groups: CommissionGroupRow[] }> {
  const res = await fetch(`/api/commissions/groups?${buildQuery(filters)}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as { period: PeriodInfo; groups: CommissionGroupRow[] };
}

export async function fetchEntries(
  filters: CommissionFilters,
): Promise<{ period: PeriodInfo; entries: CommissionEntryRow[] }> {
  const res = await fetch(`/api/commissions/entries?${buildQuery(filters)}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as { period: PeriodInfo; entries: CommissionEntryRow[] };
}

export async function setOverride(
  entryId: string,
  overrideAmount: number | null,
  overrideReason: string | null,
): Promise<CommissionEntryRow> {
  const res = await fetch(`/api/commissions/${entryId}/override`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ override_amount: overrideAmount, override_reason: overrideReason }),
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(json.error ?? `HTTP ${res.status}`);
  }
  const json = (await res.json()) as { entry: CommissionEntryRow };
  return json.entry;
}

export async function approveEntry(entryId: string): Promise<CommissionEntryRow> {
  const res = await fetch(`/api/commissions/${entryId}/approve`, { method: 'POST' });
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(json.error ?? `HTTP ${res.status}`);
  }
  const json = (await res.json()) as { entry: CommissionEntryRow };
  return json.entry;
}

export async function markEntryPaid(entryId: string): Promise<CommissionEntryRow> {
  const res = await fetch(`/api/commissions/${entryId}/mark-paid`, { method: 'POST' });
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(json.error ?? `HTTP ${res.status}`);
  }
  const json = (await res.json()) as { entry: CommissionEntryRow };
  return json.entry;
}

export async function reverseEntry(
  entryId: string,
  reason: string,
): Promise<{ entry: CommissionEntryRow; reversal: CommissionEntryRow }> {
  const res = await fetch(`/api/commissions/${entryId}/reverse`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(json.error ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as { entry: CommissionEntryRow; reversal: CommissionEntryRow };
}

export function csvDownloadUrl(filters: CommissionFilters): string {
  return `/api/commissions/csv?${buildQuery(filters)}`;
}
